import { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  type RefundChargeType,
  type RefundRequestLineRow,
  type RefundSettlementOutcome,
  useApproveRefundLine,
  useDenyRefundLine,
} from "@/api/pickup-refund-requests";
import { formatCents } from "@/api/carrier-rate-bands";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// Ticket 06: The LABEL charge is presented as "Delivery" — what the customer was quoted
// and what ops calls it. The stored enum does not change.
export const REFUND_CHARGE_LABEL: Record<RefundChargeType, string> = {
  LABEL: "Delivery",
  PICKUP: "Pickup",
  DELIVERY: "Delivery",
  INSURANCE: "Insurance",
  PACKAGING: "Packaging",
  SIGNATURE: "Signature",
  PROPERTY_TYPE_SURCHARGE: "Property surcharge",
  DISCOUNT: "Discount",
  FEE: "Fee",
};

export type RefundLineAction =
  | {
      kind: "emergency-approve";
      requestId: string;
      line: RefundRequestLineRow;
    }
  | {
      kind: "reduce";
      requestId: string;
      line: RefundRequestLineRow;
    }
  | {
      kind: "emergency-reduce";
      requestId: string;
      line: RefundRequestLineRow;
    }
  | {
      kind: "deny";
      requestId: string;
      line: RefundRequestLineRow;
    };

const ACTION_COPY: Record<
  RefundLineAction["kind"],
  { title: string; submit: string }
> = {
  "emergency-approve": {
    title: "Emergency refund authorization",
    submit: "Authorize with override",
  },
  reduce: { title: "Reduce refund", submit: "Approve reduced" },
  "emergency-reduce": {
    title: "Reduce refund",
    submit: "Approve reduced with override",
  },
  deny: { title: "Deny component", submit: "Deny" },
};

const SETTLED_STATUS_LABEL: Record<
  Extract<RefundSettlementOutcome, { settled: true }>["status"],
  string
> = {
  PENDING: "Pending",
  APPROVED: "Approved",
  PARTIALLY_APPROVED: "Partially approved",
  DENIED: "Denied",
};

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error) || typeof error.response?.data !== "object") {
    return fallback;
  }
  const data = error.response.data as {
    error?: unknown;
    message?: unknown;
  } | null;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.error === "string") return data.error;
  return fallback;
}

export function settlementMessage(outcome: RefundSettlementOutcome): string {
  if (outcome.settled) {
    return `Server settled the request as ${SETTLED_STATUS_LABEL[outcome.status]}.`;
  }
  const reasons: Record<
    Exclude<RefundSettlementOutcome, { settled: true }>["reason"],
    string
  > = {
    not_ready: "The server is still waiting for unresolved components.",
    already_settled:
      "The server reports that this request was already settled.",
    refund_failed: "The server could not settle the refund.",
    partially_settled:
      "The server settled the ready components; other components remain open.",
    manual_payout_required:
      "Authorization is complete; an admin must confirm the manual payout before this is refunded.",
  };
  return reasons[outcome.reason];
}

export function reportRefundLineSettlement(
  successMessage: string,
  outcome: RefundSettlementOutcome
): void {
  const description = settlementMessage(outcome);
  if (!outcome.settled && outcome.reason === "refund_failed") {
    toast.error(`${successMessage}, but the refund failed`, { description });
    return;
  }
  toast.success(successMessage, { description });
}

export function RefundLineActionDialog({
  action,
  onClose,
}: {
  action: RefundLineAction;
  onClose: () => void;
}) {
  const { requestId, line } = action;
  const reduction =
    action.kind === "reduce" || action.kind === "emergency-reduce";
  const emergency =
    action.kind === "emergency-approve" || action.kind === "emergency-reduce";
  const denial = action.kind === "deny";
  const copy = ACTION_COPY[action.kind];
  const [amount, setAmount] = useState(
    (line.amountGrossCents / 100).toFixed(2)
  );
  const [denialReason, setDenialReason] = useState("");
  const [emergencyOverrideReason, setEmergencyOverrideReason] = useState("");
  const approveLine = useApproveRefundLine();
  const denyLine = useDenyRefundLine();
  const isPending = approveLine.isPending || denyLine.isPending;

  const submitApproval = async () => {
    const approvedGrossCents = reduction
      ? Math.round(parseFloat(amount) * 100)
      : undefined;
    if (reduction) {
      if (
        !Number.isFinite(approvedGrossCents) ||
        !approvedGrossCents ||
        approvedGrossCents <= 0
      ) {
        toast.error("Invalid amount");
        return;
      }
      if (approvedGrossCents > line.amountGrossCents) {
        toast.error("Cannot exceed the component amount", {
          description: `Maximum ${formatCents(line.amountGrossCents)}.`,
        });
        return;
      }
    }
    if (emergency && !emergencyOverrideReason.trim()) {
      toast.error("Emergency override reason required");
      return;
    }
    try {
      const result = await approveLine.mutateAsync({
        requestId,
        lineId: line.id,
        ...(approvedGrossCents !== undefined ? { approvedGrossCents } : {}),
        ...(emergency
          ? { emergencyOverrideReason: emergencyOverrideReason.trim() }
          : {}),
      });
      reportRefundLineSettlement(
        reduction ? "Line approved at reduced amount" : "Component authorized",
        result.settlement
      );
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Couldn't approve line"));
    }
  };

  const submitDenial = async () => {
    if (!denialReason.trim()) {
      toast.error("Reason required", {
        description: "Tell the customer why this component is denied.",
      });
      return;
    }
    try {
      const result = await denyLine.mutateAsync({
        requestId,
        lineId: line.id,
        denialReason: denialReason.trim(),
      });
      reportRefundLineSettlement("Line denied", result.settlement);
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Couldn't deny line"));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {copy.title} · {REFUND_CHARGE_LABEL[line.chargeType]}{" "}
            {formatCents(line.amountGrossCents)}
          </DialogTitle>
          {emergency && (
            <DialogDescription>
              You requested this refund. Record why a second Admin is
              unavailable before authorizing your own request. Once all
              components are resolved, settlement is attempted automatically.
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3">
          {reduction && (
            <div className="space-y-1">
              <Label htmlFor="refund-approved-amount" className="text-xs">
                Approve amount (CAD)
              </Label>
              <Input
                id="refund-approved-amount"
                type="number"
                step="0.01"
                min="0.01"
                max={(line.amountGrossCents / 100).toFixed(2)}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Maximum {formatCents(line.amountGrossCents)}. The difference is
                kept as a fee.
              </p>
            </div>
          )}
          {denial && (
            <div className="space-y-1">
              <Label htmlFor="refund-denial-reason" className="text-xs">
                Denial reason
              </Label>
              <Textarea
                id="refund-denial-reason"
                value={denialReason}
                onChange={(event) => setDenialReason(event.target.value)}
                placeholder="Why this component is not refunded…"
              />
            </div>
          )}
          {emergency && (
            <div className="space-y-1">
              <Label htmlFor="refund-emergency-reason" className="text-xs">
                Emergency override reason
              </Label>
              <Textarea
                id="refund-emergency-reason"
                value={emergencyOverrideReason}
                onChange={(event) =>
                  setEmergencyOverrideReason(event.target.value)
                }
                placeholder="Why is a second Admin unavailable?"
              />
              <p className="text-xs text-muted-foreground">
                This reason is stored with the refund decision for audit.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => void (denial ? submitDenial() : submitApproval())}
            disabled={
              isPending || (emergency && !emergencyOverrideReason.trim())
            }
            variant={denial ? "destructive" : "default"}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {copy.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
